import {
  Category,
  FinnairAmount,
  FinnairCart,
  FinnairPassengerItem,
  FinnairPassengerServiceSelectionItem,
  FinnairServiceBoundItem,
  FinnairServiceItem,
} from '@fcom/dapi/api/models';
import { FinnairServiceItemWithUpsell, TravelerService } from '@fcom/dapi/interfaces';
import { PreciseNumber } from '@fcom/core/utils';

export const getHiddenCategories = (selectedCategory: Category, seatBundleCategories: Set<Category>): Set<Category> => {
  if (selectedCategory === Category.SEAT) {
    return new Set([Category.SEAT, Category.BUNDLE, ...Array.from(seatBundleCategories)]);
  }
  return new Set([selectedCategory]);
};

export const getPassengerAndInfantIds = (cart: FinnairCart, passengerId: string): string[] => {
  const infant = cart.passengers?.find(({ accompanyingTravelerId }) => accompanyingTravelerId === passengerId);
  return infant ? [passengerId, infant.id] : [passengerId];
};

export const sumHiddenCategoriesTotal = (
  totalPerCategory: FinnairCart['prices']['unpaid']['services']['totalPerCategory'],
  hiddenCategories: Set<Category>,
  paxIds: string[] | null
): PreciseNumber => {
  if (!totalPerCategory) {
    return PreciseNumber.ZERO;
  }
  return totalPerCategory
    .filter((entry) => hiddenCategories.has(entry.category))
    .reduce((sum, entry) => {
      if (!paxIds) {
        return Object.values(entry.totalPerPax ?? {}).reduce(
          (paxSum, paxData) => paxSum.add(PreciseNumber.fromString(paxData?.price?.totalAmount?.amount ?? '0')),
          sum
        );
      }
      return paxIds.reduce(
        (paxSum, id) =>
          paxSum.add(PreciseNumber.fromString(entry.totalPerPax?.[id]?.price?.totalAmount?.amount ?? '0')),
        sum
      );
    }, PreciseNumber.ZERO);
};

export const sumPendingSelectionsTotal = (selectionsByBound: TravelerService[][]): PreciseNumber => {
  return selectionsByBound
    .flat()
    .filter((s) => s?.pricePerItem?.amount)
    .reduce((sum, s) => {
      const quantity = s.quantity ?? 1;
      const itemPrice = PreciseNumber.fromString(s.pricePerItem.amount);
      const itemTotal = quantity === 1 ? itemPrice : itemPrice.multiply(PreciseNumber.from(quantity, 0));
      return sum.add(itemTotal);
    }, PreciseNumber.ZERO);
};

export const calculateDirtyTotal = (
  cart: FinnairCart,
  selectedCategory: Category,
  seatBundleCategories: Set<Category>,
  pendingSelections: TravelerService[][],
  passengerId?: string
): FinnairAmount => {
  const unpaid = cart?.prices?.unpaid;
  const currencyCode = unpaid?.total?.total?.totalAmount?.currencyCode ?? 'EUR';
  const hiddenCategories = getHiddenCategories(selectedCategory, seatBundleCategories);
  const paxIds = passengerId ? getPassengerAndInfantIds(cart, passengerId) : null;

  const baseTotal = !paxIds
    ? PreciseNumber.fromString(unpaid?.total?.total?.totalAmount?.amount ?? '0')
    : paxIds.reduce(
        (sum, id) => sum.add(PreciseNumber.fromString(unpaid?.total?.totalPerPax?.[id]?.totalAmount?.amount ?? '0')),
        PreciseNumber.ZERO
      );

  const hiddenTotal = sumHiddenCategoriesTotal(unpaid?.services?.totalPerCategory, hiddenCategories, paxIds);
  const pendingTotal = sumPendingSelectionsTotal(pendingSelections);

  const total = baseTotal.minus(hiddenTotal).add(pendingTotal);
  return { amount: total.toString(), currencyCode };
};

export const calculateQuantityForPassenger = (boundData: FinnairServiceBoundItem): number =>
  boundData.segments.reduce(
    (sum, segment) => sum + segment.passengers.reduce((paxSum, pax) => paxSum + (pax.quantity ?? 0), 0),
    0
  );

export const calculatePriceForPassenger = (boundData: FinnairServiceBoundItem): FinnairAmount => ({
  ...boundData.totalPrice,
  amount: String(
    boundData.segments.reduce(
      (sum, segment) =>
        sum.add(
          segment.passengers.reduce(
            (paxSum, pax) => paxSum.add(PreciseNumber.fromString(pax.totalPrice?.amount ?? '0')),
            PreciseNumber.ZERO
          )
        ),
      PreciseNumber.ZERO
    )
  ),
});

export const filterServiceItemByPassenger = (
  item: FinnairServiceItem,
  passengerId: string | null,
  cartPassengers: FinnairPassengerItem[]
): FinnairServiceItem => {
  if (!passengerId) {
    return item;
  }
  const infant = cartPassengers.find((pax) => pax.accompanyingTravelerId === passengerId);
  return {
    ...item,
    bounds: item.bounds.map((boundItem) => ({
      ...boundItem,
      segments: boundItem.segments.map((segment) => ({
        ...segment,
        passengers: segment.passengers.filter(({ id }) => id === passengerId || id === infant?.id),
      })),
    })),
  };
};

export const getSelectedSeatNumbers = (categoryItem: FinnairServiceItemWithUpsell): string[] =>
  categoryItem.bounds
    .flatMap(({ segments }) => segments)
    .flatMap(({ passengers }) => passengers)
    .flatMap(({ services }) => services)
    .flatMap(({ seatNumber }) => seatNumber ?? []);

export const hasMultipleVariants = (services: FinnairPassengerServiceSelectionItem[]): boolean => {
  const uniqueVariants = new Set(services.map(({ variant, seatType }) => variant ?? seatType));
  return uniqueVariants.size > 1;
};

export const getServicesForBoundAndPassenger = (
  boundData: FinnairServiceBoundItem,
  passengerId: string | null
): FinnairPassengerServiceSelectionItem[] =>
  boundData.segments.flatMap((segment) =>
    segment.passengers.flatMap((passenger) => {
      if (passengerId && passenger.id !== passengerId) {
        return [];
      }
      return passenger.services;
    })
  );

const BENEFIT_LABEL_REGEX = /^(\d+)(\s*x?\s*)/i;

export const multiplyLabelAmount = (label: string, passengerCount: number): string => {
  const match = label.match(BENEFIT_LABEL_REGEX);
  if (match) {
    const originalNumber = parseInt(match[1], 10);
    const multipliedNumber = originalNumber * passengerCount;
    return label.replace(/^\d+/, String(multipliedNumber));
  }
  return label;
};
